import { useState, useEffect } from "react";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";
import { useAuth } from "@/contexts/AuthContext";

interface GoogleCalendarIntegration {
  isConnected: boolean;
  calendarId?: string;
  accessToken?: string;
  refreshToken?: string;
  connectedAt?: string;
}

interface UseGoogleCalendarOptions {
  /** Custom redirect URI for OAuth callback (defaults to /dashboard/business-settings) */
  redirectUri?: string;
}

export function useGoogleCalendar(options: UseGoogleCalendarOptions = {}) {
  const [integration, setIntegration] = useState<GoogleCalendarIntegration>({ isConnected: false });
  const [isLoading, setIsLoading] = useState(true);
  const [isConnecting, setIsConnecting] = useState(false);
  const { user } = useAuth();
  
  const { redirectUri = `${window.location.origin}/dashboard/business-settings` } = options;

  useEffect(() => {
    if (user) {
      fetchIntegration();
    }
  }, [user]);

  const fetchIntegration = async () => {
    try {
      setIsLoading(true);
      
      // Check if user has Google Calendar integration
      const { data: profile, error } = await supabase
        .from('business_profiles')
        .select('google_calendar_id, google_access_token, google_refresh_token, google_calendar_connected_at')
        .eq('user_id', user?.id)
        .maybeSingle();

      if (error) {
        console.error('Error fetching Google Calendar integration:', error);
        setIntegration({ isConnected: false });
        return;
      }

      if (profile && profile.google_calendar_id) {
        setIntegration({
          isConnected: true,
          calendarId: profile.google_calendar_id,
          accessToken: profile.google_access_token || undefined,
          refreshToken: profile.google_refresh_token || undefined,
          connectedAt: profile.google_calendar_connected_at || undefined,
        });
      } else {
        setIntegration({ isConnected: false });
      }
    } catch (error) {
      console.error('Error fetching Google Calendar integration:', error);
      setIntegration({ isConnected: false });
    } finally {
      setIsLoading(false);
    }
  };

  const connectGoogleCalendar = async () => {
    if (!user) {
      toast.error("Please sign in to connect Google Calendar");
      return;
    }

    setIsConnecting(true);

    try {
      // Call function to start OAuth flow
      console.log('Using redirect URI:', redirectUri);
      
      const { data, error } = await supabase.functions.invoke('google-calendar-oauth', {
        body: {
          action: 'getAuthUrl',
          redirectUri: redirectUri,
        }
      });

      if (error) {
        throw error;
      }

      if (data?.authUrl) {
        // Redirect to Google OAuth
        window.location.href = data.authUrl;
      } else {
        throw new Error('No authorization URL returned');
      }
    } catch (error: any) {
      console.error("Error connecting Google Calendar:", error);
      toast.error(error.message || "Failed to connect Google Calendar");
      setIsConnecting(false);
    }
  };

  const disconnectGoogleCalendar = async () => {
    try {
      // Update business profile to remove Google Calendar integration
      const { error } = await supabase
        .from('business_profiles')
        .update({
          google_calendar_id: null,
          google_access_token: null,
          google_refresh_token: null,
          google_calendar_connected_at: null,
        })
        .eq('user_id', user?.id);

      if (error) {
        throw error;
      }

      setIntegration({ isConnected: false });
      toast.success("Google Calendar disconnected successfully");
    } catch (error: any) {
      console.error("Error disconnecting Google Calendar:", error);
      toast.error(error.message || "Failed to disconnect Google Calendar");
    }
  };

  const syncBookingToCalendar = async (bookingData: {
    title: string;
    description?: string;
    location?: string;
    startTime: string;
    endTime: string;
    customerEmail?: string;
  }) => {
    if (!integration.isConnected || !integration.calendarId) {
      console.warn("Google Calendar not connected, skipping sync");
      return null;
    }

    try {
      const { data, error } = await supabase.functions.invoke('google-calendar-sync', {
        body: {
          action: 'createEvent',
          calendarId: integration.calendarId,
          event: {
            summary: bookingData.title,
            description: bookingData.description,
            ...(bookingData.location ? { location: bookingData.location } : {}),
            start: {
              dateTime: bookingData.startTime,
              timeZone: Intl.DateTimeFormat().resolvedOptions().timeZone,
            },
            end: {
              dateTime: bookingData.endTime,
              timeZone: Intl.DateTimeFormat().resolvedOptions().timeZone,
            },
            attendees: bookingData.customerEmail ? [{ email: bookingData.customerEmail }] : undefined,
          },
        }
      });

      if (error) {
        console.error("Error syncing to Google Calendar:", error);
        return null;
      }

      return data?.eventId;
    } catch (error) {
      console.error("Error syncing booking to calendar:", error);
      return null;
    }
  };

  const deleteCalendarEvent = async (eventId: string) => {
    if (!integration.isConnected || !integration.calendarId) {
      console.warn("Google Calendar not connected, skipping deletion");
      return;
    }

    try {
      await supabase.functions.invoke('google-calendar-sync', {
        body: {
          action: 'deleteEvent',
          calendarId: integration.calendarId,
          eventId,
        }
      });
    } catch (error) {
      console.error("Error deleting calendar event:", error);
    }
  };

  const checkCalendarAvailability = async (startTime: string, endTime: string) => {
    if (!integration.isConnected || !integration.calendarId) {
      return true; // If not connected, assume available
    }

    try {
      const { data, error } = await supabase.functions.invoke('google-calendar-sync', {
        body: {
          action: 'checkAvailability',
          calendarId: integration.calendarId,
          timeMin: startTime,
          timeMax: endTime,
        }
      });

      if (error) {
        console.error("Error checking calendar availability:", error);
        return true; // Default to available on error
      }

      // Log all-day events that were ignored for debugging
      if (data?.allDayEventsIgnored > 0) {
        console.log(`Ignored ${data.allDayEventsIgnored} all-day event(s) when checking availability`);
      }
      
      return data?.isAvailable !== false;
    } catch (error) {
      console.error("Error checking calendar availability:", error);
      return true;
    }
  };

  /**
   * Handle OAuth callback - exchange authorization code for tokens
   * This should be called when Google redirects back with a ?code= parameter
   */
  const handleOAuthCallback = async (code: string): Promise<{ success: boolean; calendarId?: string; error?: string }> => {
    try {
      const { data, error } = await supabase.functions.invoke('google-calendar-oauth', {
        body: {
          action: 'exchangeCode',
          code,
          redirectUri,
        }
      });

      if (error) {
        throw error;
      }

      // Refresh the integration status
      await fetchIntegration();
      
      return { success: true, calendarId: data?.calendarId };
    } catch (error: any) {
      console.error('Error exchanging OAuth code:', error);
      return { success: false, error: error.message || 'Failed to connect Google Calendar' };
    }
  };

  return {
    integration,
    isLoading,
    isConnecting,
    connectGoogleCalendar,
    disconnectGoogleCalendar,
    handleOAuthCallback,
    syncBookingToCalendar,
    deleteCalendarEvent,
    checkCalendarAvailability,
    refetch: fetchIntegration,
  };
}